import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../ui/Button';
import Input from '../ui/Input';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type FormData = z.infer<typeof schema>;

export default function LoginForm() {
  const {
    signIn,
    signInWithGoogle,
    signInWithMicrosoft,
    signInWithApple,
    signInWithFacebook,
  } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<
    'google' | 'microsoft' | 'apple' | 'facebook' | null
  >(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async ({ email, password }: FormData) => {
    try {
      await signIn(email, password);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid credentials');
    }
  };

  const handleOAuth = async (provider: 'google' | 'microsoft' | 'apple' | 'facebook') => {
    setOauthLoading(provider);
    try {
      if (provider === 'google') await signInWithGoogle();
      if (provider === 'microsoft') await signInWithMicrosoft();
      if (provider === 'apple') await signInWithApple();
      if (provider === 'facebook') await signInWithFacebook();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'OAuth sign-in failed');
      setOauthLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* OAuth */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Button
          variant="secondary"
          fullWidth
          loading={oauthLoading === 'google'}
          onClick={() => handleOAuth('google')}
          leftIcon={
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          }
        >
          Google
        </Button>
        <Button
          variant="secondary"
          fullWidth
          loading={oauthLoading === 'microsoft'}
          onClick={() => handleOAuth('microsoft')}
          leftIcon={
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path xmlns="http://www.w3.org/2000/svg" fill="#f35325" d="M0 0h10v10H0z"/>
              <path xmlns="http://www.w3.org/2000/svg" fill="#81bc06" d="M11 0h10v10H11z"/>
              <path xmlns="http://www.w3.org/2000/svg" fill="#05a6f0" d="M0 11h10v10H0z"/>
              <path xmlns="http://www.w3.org/2000/svg" fill="#ffba08" d="M11 11h10v10H11z"/>
            </svg>
          }
        >
          Microsoft
        </Button>
        <Button
          variant="secondary"
          fullWidth
          loading={oauthLoading === 'apple'}
          onClick={() => handleOAuth('apple')}
          leftIcon={
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.42c1.39.07 2.35.74 3.15.8 1.2-.24 2.35-.93 3.62-.84 1.54.12 2.7.72 3.47 1.84-3.18 1.88-2.43 5.98.48 7.13-.57 1.52-1.32 3.01-2.72 3.93zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
          }
        >
          Apple
        </Button>
        <Button
          variant="secondary"
          fullWidth
          loading={oauthLoading === 'facebook'}
          onClick={() => handleOAuth('facebook')}
          leftIcon={
            <svg className="w-4 h-4" fill="#1877F2" viewBox="0 0 24 24">
              <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.03 4.39 11.02 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.88v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.09 24 18.1 24 12.07z"/>
            </svg>
          }
        >
          Facebook
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <hr className="flex-1 border-slate-800" />
        <span className="text-xs text-slate-500">or continue with email</span>
        <hr className="flex-1 border-slate-800" />
      </div>

      {/* Email / password */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Input
          type="email"
          label="Email"
          placeholder="you@example.com"
          autoComplete="email"
          leftIcon={<Mail size={16} />}
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          type={showPassword ? 'text' : 'password'}
          label="Password"
          placeholder="••••••••"
          autoComplete="current-password"
          leftIcon={<Lock size={16} />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="text-slate-400 hover:text-slate-200"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
          error={errors.password?.message}
          {...register('password')}
        />

        <Button type="submit" fullWidth loading={isSubmitting} size="lg">
          Sign In
        </Button>

        <div className="text-right">
          <Link
            to="/reset-password"
            className="text-xs text-brand-400 hover:text-brand-300 transition-colors"
          >
            Password dimenticata?
          </Link>
        </div>
      </form>
    </div>
  );
}
